import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  CircularProgress,
  createFilterOptions,
  Stack,
  Typography,
} from '@mui/material';
import { type SuggestCompaniesResponse } from '@qdrant/qdrant-cloud-public-api/account/v1';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'debounce';
import { XIcon } from 'lucide-react';
import { memoize } from 'micro-memoize';
import { memo, useId, useMemo, useRef, useState } from 'react';
import { type Control, Controller } from 'react-hook-form';
import { accountService } from '@/api/services/account';
import { BaseInput } from '@/components/Form/BaseInput/BaseInput';
import { type FormAccountCompanySchema } from '@/components/Settings/helpers';
import { safeAsync } from '@/helpers/errors/safeAsync';

type Company = { name: string; domain: string };
type CustomCompany = { name: string; domain?: string; inputValue?: string };

const filter = createFilterOptions<CustomCompany>();
const isAddedValue = (option: CustomCompany): option is CustomCompany & { inputValue: string } =>
  'inputValue' in option && typeof option.inputValue === 'string';

function CompanyAutocompleteInner<T extends FormAccountCompanySchema>({
  control,
  defaultValue,
}: {
  control: Control<T>;
  defaultValue?: Partial<Company>;
}) {
  const fieldId = useId();
  const prevInputValueRef = useRef(defaultValue ? (defaultValue.name ?? '') : '');
  const [options, setOptions] = useState<readonly Company[]>(() => (defaultValue ? [defaultValue as Company] : []));
  const [loading, setLoading] = useState(false);

  const queryClient = useQueryClient();
  const fetchCompanies = useMemo(
    () =>
      /**
       * Debounce to avoid spamming the API while user is typing.
       * IMPORTANT: Beware the rate-limit of the API: 5 rps per IP. If you need to change the debounce delay,
       * make sure to adjust it carefully to avoid hitting the rate limit: {@link https://qdrant.atlassian.net/browse/SG-68}
       */
      // Reads `prevInputValueRef` to drop stale responses. `debounce` is opaque to the compiler,
      // so it assumes the callback may run during render.
      // oxlint-disable-next-line react/refs
      debounce(async (queryOptions: ReturnType<typeof accountService.suggestCompaniesQuery>) => {
        // Items are cached indefinitely, for optimization. We always want to make sure it is cleaned up from cache after form is submitted.
        const [error, value] = await safeAsync(
          queryClient.fetchQuery({ ...queryOptions, staleTime: Infinity, retry: false }),
        );
        if (!error) {
          const { name } = queryOptions.queryKey[1].input as { name: string };
          if (prevInputValueRef.current === name) {
            setOptions(responseItemsToOptions(value.items));
          }
        }
        setLoading(false);
      }, 350),
    [queryClient],
  );

  return (
    <Controller
      name="company"
      control={control as Control<FormAccountCompanySchema>}
      render={({ field: { onChange, value, ...props } }) => {
        if (!('company' in control._fields)) {
          throw new Error(
            'CompanyAutocomplete must be used within a react-hook-form form that includes the company field.',
          );
        }

        return (
          <Autocomplete
            {...props}
            id={fieldId}
            options={options}
            value={value ?? null}
            freeSolo={true}
            autoComplete={true}
            blurOnSelect={true}
            autoSelect={true}
            handleHomeEndKeys={true}
            isOptionEqualToValue={(option, val) => {
              // freeSolo: option/value can be a raw string typed by the user
              if (typeof option === 'string' || typeof val === 'string') {
                return option === val;
              }
              // User clicked the "Add ..." option
              if (isAddedValue(option)) {
                return option.inputValue === val.name;
              }
              // User selected an existing option
              return option.name === val.name && option.domain === val.domain;
            }}
            getOptionKey={(option) => (typeof option === 'string' ? option : (option.domain ?? option.name))}
            getOptionLabel={(option) => {
              // User typed a new value and hit enter or blurred the input without selecting an option
              if (typeof option === 'string') {
                return option;
              }
              // User clicked the "Add ..." option
              if (isAddedValue(option)) {
                return option.inputValue;
              }
              // User selected an existing option
              return option.name;
            }}
            filterOptions={(localOptions, params) => {
              const filtered = filter(localOptions, params);
              const { inputValue } = params;
              // Suggest the creation of a new value
              if (inputValue !== '') {
                const isExisting = localOptions.some((option) => inputValue === option.name);
                if (!isExisting) {
                  filtered.push({
                    inputValue,
                    name: `Add "${inputValue}"`,
                  });
                }
              }
              return filtered;
            }}
            renderOption={({ key, ...rest }, option) => (
              <Stack key={key} component="li" direction="row" {...rest} data-sentry-mask={true} sx={{ gap: 1 }}>
                <span>{option.name}</span>
                {option.domain && (
                  <Typography variant="caption" component="span">
                    ({option.domain})
                  </Typography>
                )}
              </Stack>
            )}
            renderInput={(params) => <AutocompleteInput loading={loading} name="company" {...params} />}
            onInputChange={(_, newValue, reason) => {
              if (reason !== 'input') {
                return;
              }
              prevInputValueRef.current = newValue;
              if (newValue.length > 0) {
                const queryOptions = accountService.suggestCompaniesQuery({
                  criteria: { case: 'name', value: newValue },
                });
                const prevData = queryClient.getQueryData<SuggestCompaniesResponse>(queryOptions.queryKey);
                if (prevData !== undefined) {
                  setOptions(responseItemsToOptions(prevData.items));
                  return;
                }
                setLoading(true);
                void fetchCompanies(queryOptions);
              }
              setOptions([]);
            }}
            onChange={(_, newValue) => {
              // User clears the input
              if (!newValue) {
                setOptions([]);
                onChange(newValue);
                return;
              }
              // User types a new value and hit enter or blurred the input without selecting an option
              if (typeof newValue === 'string') {
                onChange({ name: newValue });
                return;
              }
              // User clicks the "Add ..." option
              if (isAddedValue(newValue)) {
                onChange({ name: newValue.inputValue });
                return;
              }
              // User selects an existing option
              onChange(newValue);
            }}
            loading={loading}
            noOptionsText="No companies found"
            clearIcon={<XIcon size={10} />}
            fullWidth={true}
          />
        );
      }}
    />
  );
}

export const CompanyAutocomplete = memo(CompanyAutocompleteInner) as typeof CompanyAutocompleteInner;

const AutocompleteInput = memo(function AutocompleteInput({
  loading,
  slotProps: autocompleteSlotProps,
  id,
  ...props
}: AutocompleteRenderInputParams & { name: string; loading: boolean }) {
  return (
    <BaseInput
      {...props}
      data-sentry-mask={true}
      id={id}
      label="Company Name"
      slotProps={{
        htmlInput: autocompleteSlotProps.htmlInput,
        input: {
          ...autocompleteSlotProps.input,
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
              {autocompleteSlotProps.input.endAdornment}
            </>
          ),
        },
      }}
    />
  );
});

const responseItemsToOptions = memoize(function responseItemsToOptions(
  items: SuggestCompaniesResponse['items'],
): readonly Company[] {
  return items.reduce<Company[]>((acc, company) => {
    if (company.domain) {
      acc.push({ domain: company.domain, name: company.name });
    }
    return acc;
  }, []);
});
